package com.qrkontaq.ui.admin

/** Strings of the platform owner's dashboard; follows the admin-panel language. */
interface PlatformStrings {
    val title: String
    val subtitle: String
    val refresh: String
    val logout: String
    val deniedTitle: String
    val deniedText: String
    val errorText: String
    val retry: String
    val noData: String

    // KPI
    val kpiVenues: String
    val kpiVenuesActive: String
    val kpiUsers: String
    val kpiMrr: String
    val kpiRevenue30d: String
    val kpiRevenueTotal: String
    val kpiOrders30d: String
    val kpiGmv30d: String
    val kpiScans30d: String

    // Subscriptions
    val subsTitle: String
    val subsFree: String
    val subsActive: String
    val subsPastDue: String
    val subsAutoRenew: String

    // Charts
    val chartRevenue: String
    val chartNewVenues: String
    val chartNewUsers: String

    // Venues table
    val venuesTitle: String
    val colVenue: String
    val colOwner: String
    val colPlan: String
    val colStatus: String
    val colPaidUntil: String
    val colCreated: String
    val colOrders30d: String
    val colRevenue30d: String
    val colLastOrder: String

    // Destructive actions (venue / owner deletion). {name} and {email} are substituted.
    val actDeleteVenue: String
    val actDeleteOwner: String
    val delVenueTitle: String
    val delVenueText: String
    val delOwnerTitle: String
    val delOwnerText: String
    val delConfirm: String
    val delCancel: String
    val delWorking: String
    val delFailed: String

    // Payments table
    val paymentsTitle: String
    val colDate: String
    val colAmount: String
    val colType: String
    val payRecurring: String
    val payManual: String

    val planMonth: String
    val planQuarter: String
    val planYear: String

    // Web analytics
    val tabOverview: String
    val tabAnalytics: String
    val anaSubtitle: String
    val period7: String
    val period30: String
    val period90: String
    val siteAll: String
    val siteLanding: String
    val siteAdmin: String
    val sitePlatform: String
    val anaVisitors: String
    val anaVisits: String
    val anaPageviews: String
    val anaClicks: String
    val anaNewVisitors: String
    val anaOnline: String
    val anaAvgSession: String
    val anaChartTraffic: String
    val anaLegendVisitors: String
    val anaLegendPageviews: String
    val anaTopPages: String
    val anaTopClicks: String
    val anaSections: String
    val anaSectionsHint: String
    val anaLangSwitches: String
    val anaReferrers: String
    val anaUtm: String
    val anaDevices: String
    val anaBrowsers: String
    val anaLanguages: String
    val anaColPage: String
    val anaColViews: String
    val anaColVisitors: String
    val anaColElement: String
    val anaColClicks: String
    val anaDirect: String

    // Command-center navigation
    val navOverview: String
    val navTraffic: String
    val navSources: String
    val navSessions: String
    val navVenues: String
    val navPayments: String

    // Geo
    val anaCountries: String
    val anaCities: String
    val anaMapTitle: String

    // Funnel
    val funnelTitle: String
    val funnelSubtitle: String
    val funnelSessions: String
    val funnelPricing: String
    val funnelCta: String
    val funnelAdmin: String
    val funnelSignups: String

    // Sessions
    val sessionsTitle: String
    val sessionNew: String
    val sessionLive: String
    val sessionDirect: String
    val relJustNow: String
    val relMinAgo: String
    val relHourAgo: String
    val relDayAgo: String
}

/** Current platform-dashboard strings; follows the admin language switcher.
 *  Owner-only tooling: the new customer languages fall back to English. */
val PlatformT: PlatformStrings
    get() = when (adminLanguage) {
        AdminLanguage.UK -> PlatformStringsUk
        AdminLanguage.EN -> PlatformStringsEn
        AdminLanguage.PL -> PlatformStringsPl
        AdminLanguage.RU -> PlatformStringsRu
        AdminLanguage.ES, AdminLanguage.PT, AdminLanguage.FR, AdminLanguage.DE -> PlatformStringsEn
    }

object PlatformStringsUk : PlatformStrings {
    override val title = "Панель платформи"
    override val subtitle = "Підписки, заклади та гроші — все QRkontaq на одному екрані"
    override val refresh = "Оновити"
    override val logout = "Вийти"
    override val deniedTitle = "Немає доступу"
    override val deniedText = "Цей кабінет лише для власника платформи. Увійдіть з акаунта власника."
    override val errorText = "Не вдалося завантажити дані. Спробуйте ще раз."
    override val retry = "Повторити"
    override val noData = "Поки що немає даних"
    override val kpiVenues = "Закладів"
    override val kpiVenuesActive = "Активних закладів"
    override val kpiUsers = "Користувачів"
    override val kpiMrr = "MRR"
    override val kpiRevenue30d = "Дохід за 30 днів"
    override val kpiRevenueTotal = "Дохід за весь час"
    override val kpiOrders30d = "Замовлень за 30 днів"
    override val kpiGmv30d = "Оборот закладів (30 дн)"
    override val kpiScans30d = "Сканувань QR (30 дн)"
    override val subsTitle = "Підписки"
    override val subsFree = "Free"
    override val subsActive = "Активні"
    override val subsPastDue = "Прострочені"
    override val subsAutoRenew = "Автоподовження"
    override val chartRevenue = "Дохід за місяцями"
    override val chartNewVenues = "Нові заклади (30 днів)"
    override val chartNewUsers = "Нові користувачі (30 днів)"
    override val venuesTitle = "Заклади"
    override val colVenue = "Заклад"
    override val colOwner = "Власник"
    override val colPlan = "План"
    override val colStatus = "Статус"
    override val colPaidUntil = "Оплачено до"
    override val colCreated = "Створено"
    override val colOrders30d = "Замовлень (30 дн)"
    override val colRevenue30d = "Оборот (30 дн)"
    override val colLastOrder = "Останнє замовлення"
    override val actDeleteVenue = "Видалити заклад"
    override val actDeleteOwner = "Видалити власника"
    override val delVenueTitle = "Видалити заклад?"
    override val delVenueText = "«{name}» буде видалено назавжди разом із меню, столами, замовленнями, персоналом, лояльністю та медіа. Скасувати це неможливо."
    override val delOwnerTitle = "Видалити власника?"
    override val delOwnerText = "Акаунт {email} буде видалено назавжди разом з усіма його закладами. Email звільниться для нової реєстрації. Скасувати це неможливо."
    override val delConfirm = "Видалити назавжди"
    override val delCancel = "Скасувати"
    override val delWorking = "Видаляємо…"
    override val delFailed = "Не вдалося видалити. Спробуйте ще раз."
    override val paymentsTitle = "Платежі"
    override val colDate = "Дата"
    override val colAmount = "Сума"
    override val colType = "Тип"
    override val payRecurring = "автосписання"
    override val payManual = "вручну"
    override val planMonth = "Місяць"
    override val planQuarter = "Квартал"
    override val planYear = "Рік"
    override val tabOverview = "Огляд"
    override val tabAnalytics = "Аналітика"
    override val anaSubtitle = "Хто заходить на сайт, що дивиться і куди натискає"
    override val period7 = "7 днів"
    override val period30 = "30 днів"
    override val period90 = "90 днів"
    override val siteAll = "Всі розділи"
    override val siteLanding = "Лендінг"
    override val siteAdmin = "Адмінка"
    override val sitePlatform = "Платформа"
    override val anaVisitors = "Унікальні відвідувачі"
    override val anaVisits = "Візити"
    override val anaPageviews = "Перегляди сторінок"
    override val anaClicks = "Кліки"
    override val anaNewVisitors = "Нові відвідувачі"
    override val anaOnline = "Зараз на сайті"
    override val anaAvgSession = "Середня сесія"
    override val anaChartTraffic = "Трафік за днями"
    override val anaLegendVisitors = "Відвідувачі"
    override val anaLegendPageviews = "Перегляди"
    override val anaTopPages = "Популярні сторінки"
    override val anaTopClicks = "Топ кліків"
    override val anaSections = "Секції лендингу (доскрол)"
    override val anaSectionsHint = "сесії"
    override val anaLangSwitches = "Перемикання мови"
    override val anaReferrers = "Джерела переходів"
    override val anaUtm = "UTM-джерела"
    override val anaDevices = "Пристрої"
    override val anaBrowsers = "Браузери"
    override val anaLanguages = "Мови"
    override val anaColPage = "Сторінка"
    override val anaColViews = "Перегляди"
    override val anaColVisitors = "Відвідувачі"
    override val anaColElement = "Елемент"
    override val anaColClicks = "Кліків"
    override val anaDirect = "Прямі заходи"
    override val navOverview = "Огляд"
    override val navTraffic = "Трафік"
    override val navSources = "Джерела та гео"
    override val navSessions = "Сесії"
    override val navVenues = "Заклади"
    override val navPayments = "Платежі"
    override val anaCountries = "Країни"
    override val anaCities = "Міста"
    override val anaMapTitle = "Географія відвідувачів"
    override val funnelTitle = "Воронка: лендинг → реєстрація"
    override val funnelSubtitle = "Де ми втрачаємо відвідувачів на шляху до покупки"
    override val funnelSessions = "Сесії на лендингу"
    override val funnelPricing = "Догорнули до цін"
    override val funnelCta = "Натиснули CTA"
    override val funnelAdmin = "Зайшли в адмінку"
    override val funnelSignups = "Реєстрації (довідково)"
    override val sessionsTitle = "Останні сесії"
    override val sessionNew = "Новий"
    override val sessionLive = "Онлайн"
    override val sessionDirect = "прямий"
    override val relJustNow = "щойно"
    override val relMinAgo = "хв тому"
    override val relHourAgo = "год тому"
    override val relDayAgo = "дн тому"
}

object PlatformStringsEn : PlatformStrings {
    override val title = "Platform dashboard"
    override val subtitle = "Subscriptions, venues and money — all of QRkontaq on one screen"
    override val refresh = "Refresh"
    override val logout = "Log out"
    override val deniedTitle = "No access"
    override val deniedText = "This dashboard is for the platform owner only. Sign in with the owner account."
    override val errorText = "Failed to load the data. Please try again."
    override val retry = "Retry"
    override val noData = "No data yet"
    override val kpiVenues = "Venues"
    override val kpiVenuesActive = "Active venues"
    override val kpiUsers = "Users"
    override val kpiMrr = "MRR"
    override val kpiRevenue30d = "Revenue, 30 days"
    override val kpiRevenueTotal = "Revenue, all time"
    override val kpiOrders30d = "Orders, 30 days"
    override val kpiGmv30d = "Venue turnover, 30d"
    override val kpiScans30d = "QR scans, 30d"
    override val subsTitle = "Subscriptions"
    override val subsFree = "Free"
    override val subsActive = "Active"
    override val subsPastDue = "Past due"
    override val subsAutoRenew = "Auto-renew"
    override val chartRevenue = "Revenue by month"
    override val chartNewVenues = "New venues (30 days)"
    override val chartNewUsers = "New users (30 days)"
    override val venuesTitle = "Venues"
    override val colVenue = "Venue"
    override val colOwner = "Owner"
    override val colPlan = "Plan"
    override val colStatus = "Status"
    override val colPaidUntil = "Paid until"
    override val colCreated = "Created"
    override val colOrders30d = "Orders (30d)"
    override val colRevenue30d = "Turnover (30d)"
    override val colLastOrder = "Last order"
    override val actDeleteVenue = "Delete venue"
    override val actDeleteOwner = "Delete owner"
    override val delVenueTitle = "Delete venue?"
    override val delVenueText = "“{name}” will be deleted forever together with its menu, tables, orders, staff, loyalty and media. This cannot be undone."
    override val delOwnerTitle = "Delete owner?"
    override val delOwnerText = "The account {email} will be deleted forever together with every venue it owns. The email becomes free for a new sign-up. This cannot be undone."
    override val delConfirm = "Delete forever"
    override val delCancel = "Cancel"
    override val delWorking = "Deleting…"
    override val delFailed = "Failed to delete. Please try again."
    override val paymentsTitle = "Payments"
    override val colDate = "Date"
    override val colAmount = "Amount"
    override val colType = "Type"
    override val payRecurring = "auto-charge"
    override val payManual = "manual"
    override val planMonth = "Month"
    override val planQuarter = "Quarter"
    override val planYear = "Year"
    override val tabOverview = "Overview"
    override val tabAnalytics = "Analytics"
    override val anaSubtitle = "Who visits the site, what they view and where they click"
    override val period7 = "7 days"
    override val period30 = "30 days"
    override val period90 = "90 days"
    override val siteAll = "All sections"
    override val siteLanding = "Landing"
    override val siteAdmin = "Admin"
    override val sitePlatform = "Platform"
    override val anaVisitors = "Unique visitors"
    override val anaVisits = "Visits"
    override val anaPageviews = "Pageviews"
    override val anaClicks = "Clicks"
    override val anaNewVisitors = "New visitors"
    override val anaOnline = "On site now"
    override val anaAvgSession = "Avg. session"
    override val anaChartTraffic = "Traffic by day"
    override val anaLegendVisitors = "Visitors"
    override val anaLegendPageviews = "Pageviews"
    override val anaTopPages = "Top pages"
    override val anaTopClicks = "Top clicks"
    override val anaSections = "Landing sections (scroll reach)"
    override val anaSectionsHint = "sessions"
    override val anaLangSwitches = "Language switches"
    override val anaReferrers = "Referrers"
    override val anaUtm = "UTM sources"
    override val anaDevices = "Devices"
    override val anaBrowsers = "Browsers"
    override val anaLanguages = "Languages"
    override val anaColPage = "Page"
    override val anaColViews = "Views"
    override val anaColVisitors = "Visitors"
    override val anaColElement = "Element"
    override val anaColClicks = "Clicks"
    override val anaDirect = "Direct"
    override val navOverview = "Overview"
    override val navTraffic = "Traffic"
    override val navSources = "Sources & Geo"
    override val navSessions = "Sessions"
    override val navVenues = "Venues"
    override val navPayments = "Payments"
    override val anaCountries = "Countries"
    override val anaCities = "Cities"
    override val anaMapTitle = "Visitor geography"
    override val funnelTitle = "Funnel: landing → sign-up"
    override val funnelSubtitle = "Where we lose visitors on the way to a purchase"
    override val funnelSessions = "Landing sessions"
    override val funnelPricing = "Reached pricing"
    override val funnelCta = "Clicked CTA"
    override val funnelAdmin = "Entered admin"
    override val funnelSignups = "Sign-ups (for reference)"
    override val sessionsTitle = "Recent sessions"
    override val sessionNew = "New"
    override val sessionLive = "Live"
    override val sessionDirect = "direct"
    override val relJustNow = "just now"
    override val relMinAgo = "min ago"
    override val relHourAgo = "h ago"
    override val relDayAgo = "d ago"
}

object PlatformStringsPl : PlatformStrings {
    override val title = "Panel platformy"
    override val subtitle = "Subskrypcje, lokale i pieniądze — cały QRkontaq na jednym ekranie"
    override val refresh = "Odśwież"
    override val logout = "Wyloguj"
    override val deniedTitle = "Brak dostępu"
    override val deniedText = "Ten panel jest tylko dla właściciela platformy. Zaloguj się na konto właściciela."
    override val errorText = "Nie udało się załadować danych. Spróbuj ponownie."
    override val retry = "Ponów"
    override val noData = "Brak danych"
    override val kpiVenues = "Lokale"
    override val kpiVenuesActive = "Aktywne lokale"
    override val kpiUsers = "Użytkownicy"
    override val kpiMrr = "MRR"
    override val kpiRevenue30d = "Przychód, 30 dni"
    override val kpiRevenueTotal = "Przychód łącznie"
    override val kpiOrders30d = "Zamówienia, 30 dni"
    override val kpiGmv30d = "Obrót lokali, 30 dni"
    override val kpiScans30d = "Skany QR, 30 dni"
    override val subsTitle = "Subskrypcje"
    override val subsFree = "Free"
    override val subsActive = "Aktywne"
    override val subsPastDue = "Zaległe"
    override val subsAutoRenew = "Auto-odnowienie"
    override val chartRevenue = "Przychód miesięcznie"
    override val chartNewVenues = "Nowe lokale (30 dni)"
    override val chartNewUsers = "Nowi użytkownicy (30 dni)"
    override val venuesTitle = "Lokale"
    override val colVenue = "Lokal"
    override val colOwner = "Właściciel"
    override val colPlan = "Plan"
    override val colStatus = "Status"
    override val colPaidUntil = "Opłacone do"
    override val colCreated = "Utworzono"
    override val colOrders30d = "Zamówienia (30 dni)"
    override val colRevenue30d = "Obrót (30 dni)"
    override val colLastOrder = "Ostatnie zamówienie"
    override val actDeleteVenue = "Usuń lokal"
    override val actDeleteOwner = "Usuń właściciela"
    override val delVenueTitle = "Usunąć lokal?"
    override val delVenueText = "„{name}” zostanie usunięty na zawsze wraz z menu, stolikami, zamówieniami, personelem, lojalnością i mediami. Tego nie da się cofnąć."
    override val delOwnerTitle = "Usunąć właściciela?"
    override val delOwnerText = "Konto {email} zostanie usunięte na zawsze wraz ze wszystkimi jego lokalami. Email będzie wolny dla nowej rejestracji. Tego nie da się cofnąć."
    override val delConfirm = "Usuń na zawsze"
    override val delCancel = "Anuluj"
    override val delWorking = "Usuwanie…"
    override val delFailed = "Nie udało się usunąć. Spróbuj ponownie."
    override val paymentsTitle = "Płatności"
    override val colDate = "Data"
    override val colAmount = "Kwota"
    override val colType = "Typ"
    override val payRecurring = "auto-pobranie"
    override val payManual = "ręcznie"
    override val planMonth = "Miesiąc"
    override val planQuarter = "Kwartał"
    override val planYear = "Rok"
    override val tabOverview = "Przegląd"
    override val tabAnalytics = "Analityka"
    override val anaSubtitle = "Kto odwiedza stronę, co ogląda i gdzie klika"
    override val period7 = "7 dni"
    override val period30 = "30 dni"
    override val period90 = "90 dni"
    override val siteAll = "Wszystkie sekcje"
    override val siteLanding = "Landing"
    override val siteAdmin = "Panel"
    override val sitePlatform = "Platforma"
    override val anaVisitors = "Unikalni odwiedzający"
    override val anaVisits = "Wizyty"
    override val anaPageviews = "Odsłony"
    override val anaClicks = "Kliknięcia"
    override val anaNewVisitors = "Nowi odwiedzający"
    override val anaOnline = "Teraz na stronie"
    override val anaAvgSession = "Śr. sesja"
    override val anaChartTraffic = "Ruch dziennie"
    override val anaLegendVisitors = "Odwiedzający"
    override val anaLegendPageviews = "Odsłony"
    override val anaTopPages = "Popularne strony"
    override val anaTopClicks = "Top kliknięć"
    override val anaSections = "Sekcje landingu (scroll)"
    override val anaSectionsHint = "sesje"
    override val anaLangSwitches = "Zmiany języka"
    override val anaReferrers = "Źródła wejść"
    override val anaUtm = "Źródła UTM"
    override val anaDevices = "Urządzenia"
    override val anaBrowsers = "Przeglądarki"
    override val anaLanguages = "Języki"
    override val anaColPage = "Strona"
    override val anaColViews = "Odsłony"
    override val anaColVisitors = "Odwiedzający"
    override val anaColElement = "Element"
    override val anaColClicks = "Kliknięć"
    override val anaDirect = "Wejścia bezpośrednie"
    override val navOverview = "Przegląd"
    override val navTraffic = "Ruch"
    override val navSources = "Źródła i geo"
    override val navSessions = "Sesje"
    override val navVenues = "Lokale"
    override val navPayments = "Płatności"
    override val anaCountries = "Kraje"
    override val anaCities = "Miasta"
    override val anaMapTitle = "Geografia odwiedzających"
    override val funnelTitle = "Lejek: landing → rejestracja"
    override val funnelSubtitle = "Gdzie tracimy odwiedzających w drodze do zakupu"
    override val funnelSessions = "Sesje na landingu"
    override val funnelPricing = "Doszli do cennika"
    override val funnelCta = "Kliknęli CTA"
    override val funnelAdmin = "Weszli do panelu"
    override val funnelSignups = "Rejestracje (pomocniczo)"
    override val sessionsTitle = "Ostatnie sesje"
    override val sessionNew = "Nowy"
    override val sessionLive = "Online"
    override val sessionDirect = "bezpośredni"
    override val relJustNow = "przed chwilą"
    override val relMinAgo = "min temu"
    override val relHourAgo = "godz temu"
    override val relDayAgo = "dni temu"
}

object PlatformStringsRu : PlatformStrings {
    override val title = "Панель платформы"
    override val subtitle = "Подписки, заведения и деньги — весь QRkontaq на одном экране"
    override val refresh = "Обновить"
    override val logout = "Выйти"
    override val deniedTitle = "Нет доступа"
    override val deniedText = "Этот кабинет только для владельца платформы. Войдите с аккаунта владельца."
    override val errorText = "Не удалось загрузить данные. Попробуйте ещё раз."
    override val retry = "Повторить"
    override val noData = "Пока нет данных"
    override val kpiVenues = "Заведений"
    override val kpiVenuesActive = "Активных заведений"
    override val kpiUsers = "Пользователей"
    override val kpiMrr = "MRR"
    override val kpiRevenue30d = "Доход за 30 дней"
    override val kpiRevenueTotal = "Доход за всё время"
    override val kpiOrders30d = "Заказов за 30 дней"
    override val kpiGmv30d = "Оборот заведений (30 дн)"
    override val kpiScans30d = "Сканирований QR (30 дн)"
    override val subsTitle = "Подписки"
    override val subsFree = "Free"
    override val subsActive = "Активные"
    override val subsPastDue = "Просроченные"
    override val subsAutoRenew = "Автопродление"
    override val chartRevenue = "Доход по месяцам"
    override val chartNewVenues = "Новые заведения (30 дней)"
    override val chartNewUsers = "Новые пользователи (30 дней)"
    override val venuesTitle = "Заведения"
    override val colVenue = "Заведение"
    override val colOwner = "Владелец"
    override val colPlan = "План"
    override val colStatus = "Статус"
    override val colPaidUntil = "Оплачено до"
    override val colCreated = "Создано"
    override val colOrders30d = "Заказов (30 дн)"
    override val colRevenue30d = "Оборот (30 дн)"
    override val colLastOrder = "Последний заказ"
    override val actDeleteVenue = "Удалить заведение"
    override val actDeleteOwner = "Удалить владельца"
    override val delVenueTitle = "Удалить заведение?"
    override val delVenueText = "«{name}» будет удалено навсегда вместе с меню, столами, заказами, персоналом, лояльностью и медиа. Отменить это нельзя."
    override val delOwnerTitle = "Удалить владельца?"
    override val delOwnerText = "Аккаунт {email} будет удалён навсегда вместе со всеми его заведениями. Email освободится для новой регистрации. Отменить это нельзя."
    override val delConfirm = "Удалить навсегда"
    override val delCancel = "Отмена"
    override val delWorking = "Удаляем…"
    override val delFailed = "Не удалось удалить. Попробуйте ещё раз."
    override val paymentsTitle = "Платежи"
    override val colDate = "Дата"
    override val colAmount = "Сумма"
    override val colType = "Тип"
    override val payRecurring = "автосписание"
    override val payManual = "вручную"
    override val planMonth = "Месяц"
    override val planQuarter = "Квартал"
    override val planYear = "Год"
    override val tabOverview = "Обзор"
    override val tabAnalytics = "Аналитика"
    override val anaSubtitle = "Кто заходит на сайт, что смотрит и куда нажимает"
    override val period7 = "7 дней"
    override val period30 = "30 дней"
    override val period90 = "90 дней"
    override val siteAll = "Все разделы"
    override val siteLanding = "Лендинг"
    override val siteAdmin = "Админка"
    override val sitePlatform = "Платформа"
    override val anaVisitors = "Уникальные посетители"
    override val anaVisits = "Визиты"
    override val anaPageviews = "Просмотры страниц"
    override val anaClicks = "Клики"
    override val anaNewVisitors = "Новые посетители"
    override val anaOnline = "Сейчас на сайте"
    override val anaAvgSession = "Средняя сессия"
    override val anaChartTraffic = "Трафик по дням"
    override val anaLegendVisitors = "Посетители"
    override val anaLegendPageviews = "Просмотры"
    override val anaTopPages = "Популярные страницы"
    override val anaTopClicks = "Топ кликов"
    override val anaSections = "Секции лендинга (доскролл)"
    override val anaSectionsHint = "сессии"
    override val anaLangSwitches = "Переключения языка"
    override val anaReferrers = "Источники переходов"
    override val anaUtm = "UTM-источники"
    override val anaDevices = "Устройства"
    override val anaBrowsers = "Браузеры"
    override val anaLanguages = "Языки"
    override val anaColPage = "Страница"
    override val anaColViews = "Просмотры"
    override val anaColVisitors = "Посетители"
    override val anaColElement = "Элемент"
    override val anaColClicks = "Кликов"
    override val anaDirect = "Прямые заходы"
    override val navOverview = "Обзор"
    override val navTraffic = "Трафик"
    override val navSources = "Источники и гео"
    override val navSessions = "Сессии"
    override val navVenues = "Заведения"
    override val navPayments = "Платежи"
    override val anaCountries = "Страны"
    override val anaCities = "Города"
    override val anaMapTitle = "География посетителей"
    override val funnelTitle = "Воронка: лендинг → регистрация"
    override val funnelSubtitle = "Где мы теряем посетителей на пути к покупке"
    override val funnelSessions = "Сессии на лендинге"
    override val funnelPricing = "Долистали до цен"
    override val funnelCta = "Нажали CTA"
    override val funnelAdmin = "Зашли в админку"
    override val funnelSignups = "Регистрации (справочно)"
    override val sessionsTitle = "Последние сессии"
    override val sessionNew = "Новый"
    override val sessionLive = "Онлайн"
    override val sessionDirect = "прямой"
    override val relJustNow = "только что"
    override val relMinAgo = "мин назад"
    override val relHourAgo = "ч назад"
    override val relDayAgo = "дн назад"
}
